/* eslint-disable prettier/prettier */
// Excel export for the Guaranteed Payment Exposure table.
//
// Consumes the same aggregate the on-screen table renders (see
// `layouts/report/exposure`), so the workbook can never drift from the UI.

import { saveAs } from "file-saver";
import * as Excel from "exceljs";

import { applyRange } from "layouts/report/utils";

const EXCEL_EXTENSION = ".xlsx";
const NUMBER_FORMAT = '"$"#,##0.00';

const TITLE_FILL = "1A73E8";
const HEADER_FILL = "D9D9D9";
const SUBTOTAL_FILL = "EAF1FB";
const GRAND_TOTAL_FILL = "C9DDF7";

const LAST_COLUMN = "E";

// The paid-attorney breakdown is a text cell, so it carries its own formatting to
// match what the table shows on screen.
const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

const columns = [
    { header: "Originating Attorney", key: "originatingAttorney", width: 28 },
    { header: "Matter", key: "matter", width: 45 },
    { header: "Invoice(s)", key: "invoices", width: 22 },
    { header: "Attorney(s) Paid", key: "paidAttorneys", width: 38 },
    { header: "Total Exposure", key: "totalExposure", width: 16 },
];

export const exportExposureToExcel = async (exposure, dateRangeLabel) => {
    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet("Guaranteed Exposure");

    sheet.columns = columns.map(({ key, width }) => ({ key, width }));

    let row = 1;

    // Title
    sheet.mergeCells(`A${row}:${LAST_COLUMN}${row}`);
    sheet.getCell(`A${row}`).value = "Guaranteed Payment Exposure by Originating Attorney";
    sheet.getCell(`A${row}`).font = {
        name: "Calibri",
        size: 14,
        bold: "true",
        color: { argb: "FFFFFF" },
    };
    applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "fill", {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: TITLE_FILL },
    });
    sheet.getRow(row).height = 22;
    sheet.getCell(`A${row}`).alignment = { vertical: "middle" };
    row++;

    // Subtitle: what the numbers mean, and over which range they were pulled.
    sheet.mergeCells(`A${row}:${LAST_COLUMN}${row}`);
    sheet.getCell(`A${row}`).value = dateRangeLabel
        ? `Guaranteed compensation flagged as Previously Paid on uncollected invoices — ${dateRangeLabel}`
        : "Guaranteed compensation flagged as Previously Paid on uncollected invoices";
    sheet.getCell(`A${row}`).font = { name: "Calibri", size: 10, italic: true };
    row++;

    sheet.mergeCells(`A${row}:${LAST_COLUMN}${row}`);
    sheet.getCell(`A${row}`).value = `Generated ${new Date().toLocaleString()}`;
    sheet.getCell(`A${row}`).font = { name: "Calibri", size: 10, italic: true };
    row += 2;

    // Header
    sheet.getRow(row).values = columns.map((column) => column.header);
    applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "font", {
        name: "Calibri",
        size: 11,
        bold: "true",
    });
    applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "fill", {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: HEADER_FILL },
    });
    const headerRow = row;
    row++;

    exposure.attorneys.forEach((attorney) => {
        attorney.matters.forEach((matter, index) => {
            sheet.getRow(row).values = [
                // The attorney name only leads its first matter, matching the table.
                index === 0 ? attorney.name : "",
                matter.displayNumber,
                matter.invoices.join(", "),
                matter.paidAttorneys
                    .map((paid) => `${paid.name} — ${currencyFormatter.format(paid.amount || 0)}`)
                    .join("\n"),
                matter.total,
            ];
            sheet.getCell(`D${row}`).alignment = { wrapText: true, vertical: "top" };
            sheet.getCell(`E${row}`).numFmt = NUMBER_FORMAT;
            row++;
        });

        // Per-attorney subtotal
        sheet.getRow(row).values = [`${attorney.name} total`, "", "", "", attorney.total];
        applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "font", {
            name: "Calibri",
            size: 11,
            bold: "true",
        });
        applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "fill", {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: SUBTOTAL_FILL },
        });
        sheet.getCell(`E${row}`).numFmt = NUMBER_FORMAT;
        row++;
    });

    // Grand total
    sheet.getRow(row).values = ["Grand total", "", "", "", exposure.grandTotal];
    applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "font", {
        name: "Calibri",
        size: 12,
        bold: "true",
    });
    applyRange(sheet, `A${row}:${LAST_COLUMN}${row}`, "fill", {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: GRAND_TOTAL_FILL },
    });
    sheet.getCell(`E${row}`).numFmt = NUMBER_FORMAT;

    // Freeze the header so long lists stay readable while scrolling.
    sheet.views = [{ state: "frozen", ySplit: headerRow }];

    const buffer = await workbook.xlsx.writeBuffer();
    const filetype = "application/vnd.ms-excel;charset=utf-8";
    const blob = new Blob([buffer], { type: filetype });
    saveAs(blob, `Guaranteed_Exposure_${new Date().toISOString().slice(0, 10)}${EXCEL_EXTENSION}`);
};

export default exportExposureToExcel;
